import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, unref as _unref, Fragment as _Fragment, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["src", "title", "hidden"]
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = { style: {"position":"relative"} }

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import Button from '@/components_new/Button.vue'
import type { APIGameType, GameCategoriesInfoType, PlatformType } from '@/types'
import { useUserStore } from '@/store/user-store/user-store'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import Typography from '@/components_new/Typography.vue'
import Tooltip from '@/components_new/Tooltip.vue'
import { useLoggerStore } from '@/store/logger'
import { ROUTE_NAMES } from '@/router/constants'
import { initExportApi, DestroyExportApiFunction } from '@/modules/export-api'
import { useTr } from '@/composables/use-tr'
import { useGetExternalUrl } from '@/composables/url-helpers'
import { useConfig } from '@/composables/config'

interface GameDetailsModalProps {
    game?: APIGameType
    categories?: GameCategoriesInfoType
    isGameStarted: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GameContainer',
  props: {
    game: {},
    categories: {},
    isGameStarted: { type: Boolean }
  },
  emits: ["startPlayingGame", "openReportModal"],
  setup(__props: any, { emit: __emit }) {

const tr = useTr()
const { VUE_APP_HOST_URL } = useConfig()

const router = useRouter()
const route = useRoute()
const getExternalUrl = useGetExternalUrl()

const userStore = useUserStore()
const appStore = useAppStore()
const loggerStore = useLoggerStore()

const { isAuthorized, isUserInfoLoaded } = storeToRefs(userStore)
const { platform, query } = storeToRefs(appStore)

const props = __props

const exportGameURL = computed(() => {
    if (!props.game) {
        return ''
    }

    return getExternalUrl({
        name: ROUTE_NAMES.EXPORT_GAME_PAGE,
        params: {
            locale: '', // always load export page without locale
            game: props.game.hru,
        },
        // enable portal API
        query: { ...route.query, portalApi: 1 },
    })
})

const availablePlatforms = computed(() => {
    const result: PlatformType[] = []
    if (props.game?.is_desktop) {
        result.push('desktop')
    }
    if (props.game?.is_android) {
        result.push('android')
    }
    if (props.game?.is_ios) {
        result.push('ios')
    }

    return result
})

const platformTranslations = computed(() => ({
    desktop: tr('gameContainer.desktop'),
    android: tr('gameContainer.android'),
    ios: tr('gameContainer.ios'),
}))

const limitedAvailablity = computed(() => {
    if (isPlatformAvailable.value || !props.game) {
        return false
    }

    const translations = platformTranslations.value

    const firstPlatform = availablePlatforms.value[0]

    if (availablePlatforms.value.length === 1) {
        return tr('gameContainer.limitedAvailablity1', {
            platform: translations[firstPlatform],
        })
    }

    const restPlatforms = availablePlatforms.value.slice(0, -1)
    const lastPlatform = availablePlatforms.value.slice(-1)[0]

    return tr('gameContainer.limitedAvailablityMany', {
        platfroms: restPlatforms.map((p) => translations[p]).join(', '),
        lastPlatform: translations[lastPlatform],
    })
})

const isFullscreen = ref<boolean>(false)

const isFrameHidden = computed(() => platform.value !== 'desktop' && !isFullscreen.value)

const layoutStore = useLayoutStore()
const showCopied = ref(false)
const exportApiDestroy = ref<DestroyExportApiFunction>()

watch(
    () => isFullscreen.value,
    (value) => {
        layoutStore.setFullscreen(value)
    },
)

const iframeOnMount = (el: unknown | null) => {
    if (el) {
        initExportApi(el as HTMLIFrameElement, {
            authorizeUser: userStore.authorizeUser,
            getUser: userStore.getUser,
            chargeUser: userStore.chargeUser,
            closeGame: () => {
                toggleFullscreen()
            },
        }).then((destroy: DestroyExportApiFunction) => {
            exportApiDestroy.value = destroy
        })
    } else {
        exportApiDestroy.value?.()
    }
}

function preventGameLooseFocus(event: PointerEvent) {
    event.preventDefault()
}

async function toggleFullscreen(forceFullscreen?: boolean) {
    if (typeof forceFullscreen === 'boolean') {
        isFullscreen.value = forceFullscreen
    } else {
        isFullscreen.value = !isFullscreen.value
    }
    if (document.fullscreenEnabled && isDesktop.value) {
        if (isFullscreen.value) {
            try {
                await document.body.requestFullscreen()
            } catch {
                // iOS or non-gesture call fullscreen
            }
        } else {
            try {
                await document.exitFullscreen()
            } catch {
                // we might be not in fullscreen mode
            }
        }
    }
}

const emit = __emit

const isPlatformAvailable = computed(() => availablePlatforms.value.includes(platform.value))

const hasAutoplay = computed(
    // default autoplay ruins lighthouse perf on desktop
    // temporary disabling it, until better solutions
    () => query.value?.autoplay /* || isTabletPlus.value */ && isPlatformAvailable.value && !props.game?.is_hidden,
)

const isDesktop = computed(() => platform.value === 'desktop')

function startGame() {
    if (props.game?.is_hidden) {
        if (props.categories?.pathCategories[0]) {
            router.push(props.categories.pathCategories[0].href)
        }
        return
    }
    if (props.game && !props.isGameStarted) {
        if (!isDesktop.value) {
            toggleFullscreen(true)
        }
        emit('startPlayingGame')
    }
}

function handlePlay() {
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'play',
        action: 'click',
    })
    startGame()
}

function handleReport() {
    emit('openReportModal')
}

function handleShare() {
    if (!props.game) {
        return
    }
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'share',
        action: 'click',
    })
    const data = {
        title: props.game?.title,
        text: props.game?.description,
        url:
            VUE_APP_HOST_URL +
            getExternalUrl({
                name: ROUTE_NAMES.GAME_PAGE,
                params: {
                    game: props.game.hru,
                },
                query: { utm_source: 'share-game' },
            }),
    }
    if (!isDesktop.value && navigator.canShare && navigator.canShare(data)) {
        navigator.share(data)
    } else {
        navigator.clipboard.writeText(data.url).then(
            () => {
                showCopied.value = true
                setTimeout(() => {
                    showCopied.value = false
                }, 3000)
            },
            () => {
                console.error('Failed to copy text')
            },
        )
    }
}

const onFullscreenChange = () => {
    isFullscreen.value = document.fullscreenElement !== null
}

const onVisibilityChange = () => {
    if (!document.hidden) {
        startGame()
    }
}

onMounted(() => {
    if (document.fullscreenEnabled) {
        document.addEventListener('fullscreenchange', onFullscreenChange)
    }
    if (hasAutoplay.value) {
        onVisibilityChange()
        document.addEventListener('visibilitychange', onVisibilityChange)
    }
})

watch(
    () => props.game,
    () => {
        if (hasAutoplay.value) {
            onVisibilityChange()
        }
    },
)

onUnmounted(() => {
    document.removeEventListener('fullscreenchange', onFullscreenChange)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    layoutStore.setFullscreen(false)
    exportApiDestroy.value?.()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([_ctx.$style.container, { [_ctx.$style.fullscreen]: isFullscreen.value }])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(_ctx.$style.content)
    }, [
      (_ctx.game)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            (_ctx.isGameStarted)
              ? (_openBlock(), _createElementBlock("iframe", {
                  key: 0,
                  ref: iframeOnMount,
                  src: exportGameURL.value,
                  class: _normalizeClass(_ctx.$style.iframe),
                  title: _ctx.game.title,
                  allow: "fullscreen",
                  hidden: isFrameHidden.value
                }, null, 10, _hoisted_1))
              : _createCommentVNode("", true),
            (!_ctx.isGameStarted || (_ctx.isGameStarted && isFrameHidden.value))
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createElementVNode("div", {
                    class: _normalizeClass(_ctx.$style.background),
                    style: _normalizeStyle({ backgroundImage: `url(${_ctx.game.preview})` })
                  }, null, 6),
                  _createElementVNode("div", {
                    class: _normalizeClass(_ctx.$style.cover)
                  }, [
                    _createElementVNode("img", {
                      class: _normalizeClass(_ctx.$style.preview),
                      src: _ctx.game.preview,
                      alt: _ctx.game.title
                    }, null, 10, _hoisted_2),
                    _createElementVNode("div", {
                      class: _normalizeClass(_ctx.$style.cover_buttons)
                    }, [
                      (!_ctx.isGameStarted)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            (limitedAvailablity.value)
                              ? (_openBlock(), _createBlock(Typography, {
                                  key: 0,
                                  type: "paragraph",
                                  size: "m",
                                  accent: "",
                                  class: _normalizeClass(_ctx.$style.limited_availability)
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(limitedAvailablity.value), 1)
                                  ]),
                                  _: 1
                                }, 8, ["class"]))
                              : (_unref(isUserInfoLoaded) && !_unref(isAuthorized))
                                ? (_openBlock(), _createBlock(Button, {
                                    key: 1,
                                    size: "l",
                                    width: "full",
                                    effect: "bevel",
                                    color: "golden",
                                    interfaceTarget: "play",
                                    onClick: handlePlay
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_ctx.$tr('gameContainer.playNoAuth')), 1)
                                    ]),
                                    _: 1
                                  }))
                                : (_openBlock(), _createBlock(Button, {
                                    key: 2,
                                    size: "l",
                                    width: "full",
                                    effect: "bevel",
                                    color: "golden",
                                    interfaceTarget: "play",
                                    onClick: handlePlay
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_ctx.$tr('gameContainer.play')), 1)
                                    ]),
                                    _: 1
                                  }))
                          ], 64))
                        : (_openBlock(), _createBlock(Button, {
                            key: 1,
                            size: "l",
                            width: "full",
                            effect: "bevel",
                            color: "golden",
                            interfaceTarget: "play",
                            onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleFullscreen()))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$tr('gameContainer.continuePlay')), 1)
                            ]),
                            _: 1
                          }))
                    ], 2)
                  ], 2)
                ], 64))
              : _createCommentVNode("", true)
          ], 64))
        : _createCommentVNode("", true)
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass(_ctx.$style.footer)
    }, [
      (props.game)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(Typography, {
              is: "div",
              type: "label",
              size: "s",
              accent: true,
              class: _normalizeClass(_ctx.$style.title)
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(props.game.title), 1)
              ]),
              _: 1
            }, 8, ["class"]),
            _createElementVNode("div", {
              class: _normalizeClass(_ctx.$style.pannel_spacer)
            }, null, 2),
            _createVNode(Button, {
              class: _normalizeClass(_ctx.$style.report_button_wide),
              visual: "text",
              color: "carbon-400",
              iconLeft: "report",
              interfaceTarget: "report",
              onClick: handleReport
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$tr('gameContainer.report')), 1)
              ]),
              _: 1
            }, 8, ["class"]),
            _createVNode(Button, {
              class: _normalizeClass(_ctx.$style.report_button_short),
              visual: "text",
              color: "carbon-400",
              icon: "report",
              interfaceTarget: "report",
              onClick: handleReport
            }, null, 8, ["class"]),
            _createElementVNode("div", _hoisted_3, [
              _createVNode(Tooltip, {
                content: _ctx.$tr('gameContainer.link_copied'),
                shown: showCopied.value
              }, {
                default: _withCtx(() => [
                  _createVNode(Button, {
                    visual: "text",
                    color: "carbon-400",
                    iconLeft: "share",
                    interfaceTarget: "share-game",
                    onClick: handleShare
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$tr('gameContainer.share')), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["content", "shown"])
            ]),
            (isDesktop.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  (!isFullscreen.value)
                    ? (_openBlock(), _createBlock(Button, {
                        key: 0,
                        visual: "text",
                        color: "carbon-400",
                        iconLeft: "fullscreen",
                        interfaceTarget: "fullscreen",
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (toggleFullscreen())),
                        onPointerdown: preventGameLooseFocus
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$tr('gameContainer.fullscreen')), 1)
                        ]),
                        _: 1
                      }))
                    : (_openBlock(), _createBlock(Button, {
                        key: 1,
                        visual: "text",
                        color: "carbon-400",
                        iconLeft: "collapse",
                        interfaceTarget: "fullscreen",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (toggleFullscreen())),
                        onPointerdown: preventGameLooseFocus
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$tr('gameContainer.fullscreen')), 1)
                        ]),
                        _: 1
                      }))
                ], 64))
              : _createCommentVNode("", true)
          ], 64))
        : _createCommentVNode("", true)
    ], 2)
  ], 2))
}
}

})